import React, { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import PropTypes from "prop-types";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

const Carousel = forwardRef(function Carousel(
  {
    items = [],
    initialIndex = 0,
    autoplay = true,
    intervalMs = 4000,
    pauseOnHover = true,
    showDots = true,
    showArrows = true,
    aspectClassName = "aspect-[16/9]",
    containerClassName = "",
    slideClassName = "",
    onIndexChange,
    onSlideClick,
  },
  ref
) {
  const [currentIndex, setCurrentIndex] = useState(clamp(initialIndex, 0, Math.max(items.length - 1, 0)));
  const [isHovering, setIsHovering] = useState(false);
  const autoplayTimerRef = useRef(null);
  const pointerStartRef = useRef(null);
  const containerRef = useRef(null);
  const videoRefs = useRef({});
  const youtubeIframeRef = useRef(null);
  const prevIndexRef = useRef(currentIndex);
  const [isInView, setIsInView] = useState(false);

  const postMessageToYouTube = (iframe, func, args = []) => {
    try {
      if (!iframe || !iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args }),
        "*"
      );
    } catch {
      // ignore
    }
  };
  const registerYouTubeEvents = (iframe) => {
    try {
      if (!iframe || !iframe.contentWindow) return;
      iframe.contentWindow.postMessage(JSON.stringify({ event: "listening" }), "*");
      iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
    } catch {
      // ignore
    }
  };
  const parseYouTubeId = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) {
        return u.pathname.replace("/", "");
      }
      if (u.hostname.includes("youtube.com")) {
        if (u.pathname === "/watch") {
          return u.searchParams.get("v");
        }
        // e.g. /embed/{id}
        const parts = u.pathname.split("/");
        const idx = parts.indexOf("embed");
        if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      }
    } catch (e) {
      // ignore
    }
    // fallback: try regex
    const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
  };

  const hasItems = items && items.length > 0;

  const goTo = (index) => {
    if (!hasItems) return;
    const bounded = ((index % items.length) + items.length) % items.length;
    setCurrentIndex(bounded);
    if (onIndexChange) onIndexChange(bounded);
  };

  const next = () => goTo(currentIndex + 1);
  const prev = () => goTo(currentIndex - 1);

  useImperativeHandle(ref, () => ({ goTo, next, prev, getIndex: () => currentIndex }), [currentIndex]);

  // 切換投影片時處理 YouTube 自動播放與暫停
  useEffect(() => {
    const prevIndex = prevIndexRef.current;
    const prevItem = items?.[prevIndex];
    const currItem = items?.[currentIndex];

    if (prevItem && prevItem.type === "youtube") {
      if (youtubeIframeRef.current) {
        postMessageToYouTube(youtubeIframeRef.current, "pauseVideo");
      }
    }

    if (currItem && currItem.type === "youtube") {
      if (isInView && youtubeIframeRef.current) {
        registerYouTubeEvents(youtubeIframeRef.current);
        postMessageToYouTube(youtubeIframeRef.current, "mute");
        postMessageToYouTube(youtubeIframeRef.current, "playVideo");
      }
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
    } else if (currItem && currItem.type === "video") {
      const vid = videoRefs.current[currentIndex];
      if (isInView && vid) {
        try {
          vid.muted = true;
          const p = vid.play();
          if (p && typeof p.catch === "function") p.catch(() => undefined);
        } catch (e) { void e; }
      }
    }

    prevIndexRef.current = currentIndex;
  }, [currentIndex, items, isInView]);

  // 監聽 YouTube 播放狀態，結束後自動切到下一張
  useEffect(() => {
    const onMessage = (event) => {
      let data = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        data = null;
      }
      if (!data || data.event !== "onStateChange" || data.info !== 0) return; // 0: ended
      if (!youtubeIframeRef.current) return;
      try {
        if (event.source !== youtubeIframeRef.current.contentWindow) return;
      } catch {
        return;
      }
      next();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // 初次載入或進入視窗時若當前為影片或 YouTube，自動播放
  useEffect(() => {
    if (!hasItems || !isInView) return;
    const curr = items[currentIndex];
    const timer = setTimeout(() => {
      if (curr?.type === "video") {
        const vid = videoRefs.current[currentIndex];
        if (vid) {
          try {
            vid.muted = true;
            const p = vid.play();
            if (p && typeof p.catch === "function") p.catch(() => undefined);
          } catch (e) { void e; }
        }
      } else if (curr?.type === "youtube") {
        if (youtubeIframeRef.current) {
          registerYouTubeEvents(youtubeIframeRef.current);
          postMessageToYouTube(youtubeIframeRef.current, "mute");
          postMessageToYouTube(youtubeIframeRef.current, "playVideo");
        }
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [hasItems, isInView, currentIndex]); 

  // 觀察輪播是否進入視窗；進入時播放，離開時暫停
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInView(entry.isIntersecting && entry.intersectionRatio >= 0.3);
      },
      { threshold: [0, 0.25, 0.3, 0.5, 1] }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);

  // 離開視窗時暫停目前媒體
  useEffect(() => {
    if (isInView) return;
    const curr = items?.[currentIndex];
    if (!curr) return;
    if (curr.type === "video") {
      const vid = videoRefs.current[currentIndex];
      if (vid && typeof vid.pause === "function") {
        try { vid.pause(); } catch (e) { void e; }
      }
    } else if (curr.type === "youtube") {
      if (youtubeIframeRef.current) {
        postMessageToYouTube(youtubeIframeRef.current, "pauseVideo");
      }
    }
  }, [isInView, currentIndex, items]);

  useEffect(() => {
    if (!autoplay || !hasItems) return;
    if (pauseOnHover && isHovering) return;
    const currentItem = items[currentIndex];
    // 若為影片且正在播放，暫停自動輪播；若為 YouTube，避免自動切換
    if (currentItem && (currentItem.type === "video" || currentItem.type === "youtube")) {
      const vid = videoRefs.current[currentIndex];
      if (currentItem.type === "youtube") return;
      if (vid && !vid.paused && !vid.ended) return;
    }
    autoplayTimerRef.current = setTimeout(next, intervalMs);
    return () => clearTimeout(autoplayTimerRef.current);
  }, [autoplay, intervalMs, isHovering, currentIndex, hasItems, pauseOnHover, items]);

  const handlePointerDown = (e) => {
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    pointerStartRef.current = { x: startX, time: Date.now() };
  };

  const handlePointerMove = (e) => {
    // 保留空實作以後續做拖曳位移特效
  };

  const handlePointerUp = (e) => {
    if (!pointerStartRef.current) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const deltaX = endX - pointerStartRef.current.x;
    const elapsed = Date.now() - pointerStartRef.current.time;
    pointerStartRef.current = null;
    const threshold = 40; // 滑動距離閾值
    const timeThreshold = 600; // 時間閾值
    if (Math.abs(deltaX) > threshold && elapsed < timeThreshold) {
      if (deltaX < 0) next();
      else prev();
    }
  };

  const currentItem = useMemo(() => (hasItems ? items[currentIndex] : null), [hasItems, items, currentIndex]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${aspectClassName} ${containerClassName}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
    >
      {/* Slide container */}
      <div className="absolute inset-0 bg-black">
        {currentItem && currentItem.type === "image" && (
          <img
            src={currentItem.src}
            alt={currentItem.alt || ""}
            className={`w-full h-full object-contain ${slideClassName} cursor-${onSlideClick ? "zoom-in" : "default"}`}
            onClick={() => onSlideClick && onSlideClick(currentIndex)}
          />)
        }
        {currentItem && currentItem.type === "video" && (
          <video
            ref={(el) => (videoRefs.current[currentIndex] = el)}
            className={`w-full h-full object-contain ${slideClassName}`}
            controls
            muted
            playsInline
            onPlay={() => {
              // 播放時暫停自動輪播計時
              if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
            }}
            onEnded={() => {
              // 結束後自動下一張
              next();
            }}
          >
            <source src={currentItem.src} type="video/mp4" />
          </video>
        )}
        {currentItem && currentItem.type === "youtube" && (
          <iframe
            ref={(el) => (youtubeIframeRef.current = el)}
            title={currentItem.alt || "YouTube video"}
            className={`w-full h-full ${slideClassName}`}
            onLoad={() => {
              if (youtubeIframeRef.current) registerYouTubeEvents(youtubeIframeRef.current);
            }}
            src={
              (() => {
                const id = parseYouTubeId(currentItem.src);
                const base = id ? `https://www.youtube.com/embed/${id}` : currentItem.src;
                const origin =
                  typeof window !== "undefined" && window.location && window.location.origin
                    ? `&origin=${encodeURIComponent(window.location.origin)}`
                    : "";
                return `${base}?rel=0&playsinline=1&enablejsapi=1${origin}`;
              })()
            }
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}
      </div>

      {/* Arrows */}
      {showArrows && hasItems && items.length > 1 && (
        <>
          <button
            type="button"
            aria-label="上一張"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full w-10 h-10 flex items-center justify-center z-20"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            <img src="/SliderArrow-left.svg" alt="prev" style={{ width: 20, height: 'auto' }} />
          </button>
          <button
            type="button"
            aria-label="下一張"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full w-10 h-10 flex items-center justify-center z-20"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            <img src="/SliderArrow-right.svg" alt="next" style={{ width: 20, height: 'auto' }} />
          </button>
        </>
      )}

      {/* Dots */}
      {showDots && hasItems && items.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`前往第 ${idx + 1} 張`}
              className={`w-2.5 h-2.5 rounded-full border border-white ${idx === currentIndex ? "bg-white" : "bg-transparent"}`}
              onClick={(e) => {
                e.stopPropagation();
                goTo(idx);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
});

Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["image", "video", "youtube"]).isRequired,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ),
  initialIndex: PropTypes.number,
  autoplay: PropTypes.bool,
  intervalMs: PropTypes.number,
  pauseOnHover: PropTypes.bool,
  showDots: PropTypes.bool,
  showArrows: PropTypes.bool,
  aspectClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  slideClassName: PropTypes.string,
  onIndexChange: PropTypes.func,
  onSlideClick: PropTypes.func,
};

export default Carousel;


